import { Divider } from 'component-library';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement, PlotReportFactLabelNameEnum, R1FactType } from '@/api/rest/resources/types/fact';
import {
  MapRadiusContext,
  NO_RADIUS,
  RADIUS_LABEL_MAPPING,
  RadiusType,
  useMapRadiusContext,
} from '@/components/MapOverview/components/MapRadiusContextProvider';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { ThreatenedSpeciesExplainer } from '@/components/NCExplainers/ThreatenedSpeciesExplainer';
import { COMPLIANCE_LOGOS_THREATENED_SPECIES } from '@/config/constants';
import { usePlotFacts, usePlotFactsByRadius } from '@/pages/shared/hooks/usePlotFact';
import { getFactsFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';

import { useLandscapeViewContext } from '../../LandscapeViewContextProvider';
import { ThreatenedSpeciesGraphTile } from './GraphTile';
import { ThreatenedSpeciesStatsTile } from './StatsTile';

const FACT_NAMES = {
  graph: R1FactType.species_presence_aggregated_count,
  aggregatedCount: R1FactType.species_presence_aggregated_count_by_species,
};

export const ThreatenedSpecies = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper
      name={t('shared.ncaDetail.details.threatenedSpecies.labels.threatenedSpecies')}
      errorFallBack={<></>}
    >
      <ThreatenedSpeciesRenderer />
    </PlotCardWrapper>
  );
};

const ThreatenedSpeciesRenderer = () => {
  const hasRadiusContext = !!useContext(MapRadiusContext);

  switch (hasRadiusContext) {
    case true:
      return <ThreatenedSpeciesBaseWithRadius />;
    case false:
    default:
      return <ThreatenedSpeciesBaseWithoutRadius />;
  }
};

const ThreatenedSpeciesBaseWithRadius = () => {
  const { isLandscapeActive } = useLandscapeViewContext();
  const { radius } = useMapRadiusContext();

  const facts = usePlotFactsByRadius(Object.values(FACT_NAMES));

  if (!isLandscapeActive) {
    return null;
  }

  return <ThreatenedSpeciesBase radius={radius} facts={facts} />;
};

const ThreatenedSpeciesBaseWithoutRadius = () => {
  const facts = usePlotFacts(Object.values(FACT_NAMES), undefined, undefined, {
    [PlotReportFactLabelNameEnum.landscape_radius_m]: RADIUS_LABEL_MAPPING[`${NO_RADIUS}`],
  });

  return <ThreatenedSpeciesBase facts={facts} />;
};

const ThreatenedSpeciesBase = ({
  radius,
  facts,
}: {
  radius?: RadiusType;
  facts: PlotReportFactElement<unknown>[] | undefined;
}) => {
  const { t } = useTranslation();

  const graphData = getFactsFromFactArray(facts, FACT_NAMES.graph) ?? [];
  const statsData = getFactsFromFactArray(facts, FACT_NAMES.aggregatedCount) ?? [];

  if (graphData.length === 0 && statsData.length === 0) {
    return null;
  }

  return (
    <NCCard explainer={<ThreatenedSpeciesExplainer />} data-testid='threatened-species-nca'>
      <NCCardHeader
        title={t('shared.ncaDetail.details.threatenedSpecies.labels.threatenedSpecies')}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <ThreatenedSpeciesGraphTile threatenedSpecies={graphData} radius={radius} />
        <ThreatenedSpeciesStatsTile threatenedSpeciesSummary={statsData} radius={radius} />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_THREATENED_SPECIES} />
    </NCCard>
  );
};
